// UpdateMapChargesComponent.js
import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import styles from "./CustomUpdateMapCharges.module.css";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import leftArrow from "../../../assests/leftArrow.svg";

// Helper: format value type
const formatValueType = (valueType) => {
  if (!valueType) return "N/A";
  if (valueType === "amount_percentage") return "Percentage (%)";
  if (valueType === "amount_flat") return "Flat Amount";
  if (valueType === "fixed") return "Fixed";
  if (valueType === "percentage") return "Percentage (%)";
  return valueType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Helper: format charge type
const formatChargeType = (chargeType) => {
  if (!chargeType) return "N/A";
  return chargeType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const UpdateMapChargesComponent = (props) => {
  const navigate = useNavigate();

  const goback = () => navigate(-1);

  return (
    <div className={styles.customChargesContainer}>
      <Sidebar />

      <div className={styles.customChargesContent}>
        {/* ============ HEADER ============ */}
        <div className={styles.headerSection}>
          <button
            className={styles.backButton}
            onClick={goback}
            type="button"
          >
            <img src={leftArrow} alt="Back" />
          </button>
          <div className={styles.titleBlock}>
            <h2 className={styles.pageTitle}>Edit Mapped Charges</h2>
            <p className={styles.pageSubtitle}>
              Select charges you want to remove from this franchisee
            </p>
          </div>
        </div>

        {/* ============ CARDS WRAPPER ============ */}
        <div className={styles.chargesGrid}>
          {props.updateMapCharges?.length > 0 ? (
            <div className={styles.cardsContainer}>
              {props.updateMapCharges.map((charge, index) => {
                const isSelected = props.selectedChargeIds.includes(charge.id);
                const valueTypeLabel = formatValueType(charge.valueType);
                const isPercentage = charge.valueType === "amount_percentage";

                return (
                  <div
                    key={charge.id}
                    className={`${styles.chargeCard} ${
                      isSelected ? styles.chargeCardSelected : ""
                    }`}
                    onClick={() => props.handleCheckboxChange(charge.id)}
                  >
                    {/* Selection Ribbon */}
                    {isSelected && (
                      <div className={styles.selectedRibbon}>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <div className={styles.chargeIconCircle}>
                          <span>💳</span>
                        </div>
                        <div>
                          <div className={styles.chargeNumber}>
                            Charge #{index + 1}
                          </div>
                          <div className={styles.chargeNameHeader}>
                            {charge.name || "Unnamed Charge"}
                          </div>
                        </div>
                      </div>

                      <label
                        className={styles.checkboxWrap}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            props.handleCheckboxChange(charge.id)
                          }
                          className={styles.chargeCheckbox}
                        />
                        <span className={styles.checkboxMark}></span>
                      </label>
                    </div>

                    {/* Value Block */}
                    <div className={styles.valueBlock}>
                      <div className={styles.valueBlockLabel}>
                        Charge Value
                      </div>
                      <div className={styles.valueBlockValue}>
                        {charge.value ?? "—"}
                        {isPercentage && (
                          <span className={styles.percentSign}>%</span>
                        )}
                      </div>
                      <div className={styles.valueTypeTag}>
                        {valueTypeLabel}
                      </div>
                    </div>

                    {/* Details Section */}
                    <div className={styles.detailsSection}>
                      <div className={styles.detailsSectionTitle}>
                        Configuration
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Charge ID</span>
                        <span className={styles.detailValue}>
                          {charge.id}
                        </span>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Charge Type</span>
                        <span
                          className={`${styles.detailValue} ${styles.typeBadge}`}
                        >
                          {formatChargeType(charge.chargeType)}
                        </span>
                      </div>

                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                          Beneficiary Type ID
                        </span>
                        <span className={styles.detailValue}>
                          {charge.beneficiaryTypeId ?? "—"}
                        </span>
                      </div>

                      {charge.deductionTypeId && (
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>
                            Deduction Type ID
                          </span>
                          <span className={styles.detailValue}>
                            {charge.deductionTypeId}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Value Range */}
                    {(charge.valueMin !== null ||
                      charge.valueMax !== null) && (
                      <div className={styles.rangeSection}>
                        <div className={styles.rangeItem}>
                          <span className={styles.rangeLabel}>Min</span>
                          <span className={styles.rangeValue}>
                            {charge.valueMin ?? "—"}
                          </span>
                        </div>
                        <div className={styles.rangeDivider}></div>
                        <div className={styles.rangeItem}>
                          <span className={styles.rangeLabel}>Max</span>
                          <span className={styles.rangeValue}>
                            {charge.valueMax ?? "—"}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {charge.description && (
                      <div className={styles.descriptionSection}>
                        <span className={styles.descriptionIcon}>📝</span>
                        <span className={styles.descriptionText}>
                          {charge.description}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIconWrap}>
                <span className={styles.emptyIcon}>💳</span>
              </div>
              <p className={styles.emptyTitle}>No mapped charges</p>
              <p className={styles.emptyText}>
                There are no mapped charges to edit at this moment.
              </p>
            </div>
          )}
        </div>

        {/* ============ ACTION BUTTONS ============ */}
        <div className={styles.actionButtons}>
          <Button
            variant="outlined"
            onClick={goback}
            className={styles.backBtn}
          >
            Back
          </Button>
          <Button
            onClick={props.handleUpdateMapCharges}
            className={styles.removeBtn}
          >
            Remove Selected
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateMapChargesComponent;